/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react"
import { invalidateAllImageCaches } from "utils/remoteImageCache"
import { withBasename } from "appBasePath"

// react-router components
import { useNavigate, useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types"

// @material-ui core components
import AppBar from "@mui/material/AppBar"
import Toolbar from "@mui/material/Toolbar"
import IconButton from "@mui/material/IconButton"
import Menu from "@mui/material/Menu"
import Icon from "@mui/material/Icon"

// Material Dashboard 2 React components
import MDBox from "components/MDBox"
import MDBadge from "components/MDBadge"

// Material Dashboard 2 React example components
import Breadcrumbs from "monitor/Breadcrumbs"
import NotificationItem from "monitor/Items/NotificationItem"
import NotificationCard from "monitor/Items/NotificationCard"

// Centro de Notificaciones
import { useNotifications } from "context/NotificationContext"

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "monitor/Navbars/DashboardNavbar/styles"

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context"
import { Divider } from "@mui/material"
import MDTypography from "components/MDTypography"

function DashboardNavbar({ absolute, light, isMini }) {
  const role = sessionStorage.getItem("role") || localStorage.getItem("role")
  const username = sessionStorage.getItem("username") || localStorage.getItem("username")

  const [navbarType, setNavbarType] = useState()
  const [controller, dispatch] = useMaterialUIController()
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller
  const [openMenu, setOpenMenu] = useState(false)
  const [openMenuUser, setOpenMenuUser] = useState(false)
  const route = useLocation().pathname.split("/").slice(1)
  const navigate = useNavigate()

  const { items: notifications, unreadCount, markRead, markAllRead } = useNotifications()

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky")
    } else {
      setNavbarType("static")
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar)
    }

    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar()

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar)
  }, [dispatch, fixedNavbar])

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav)
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator)
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget)
  const handleCloseMenu = () => setOpenMenu(false)
  const handleOpenMenuUser = (event) => setOpenMenuUser(event.currentTarget)
  const handleCloseMenuUser = () => setOpenMenuUser(false)

  const Logout = () => {
    invalidateAllImageCaches()
    localStorage.removeItem("token")
    localStorage.removeItem("role")
    localStorage.removeItem("username")
    localStorage.removeItem("company")
    localStorage.removeItem("companyId")
    sessionStorage.removeItem("token")
    sessionStorage.removeItem("role")
    sessionStorage.removeItem("username")
    sessionStorage.removeItem("company")
    sessionStorage.removeItem("companyId")
    sessionStorage.removeItem("embed.session.active")
    sessionStorage.removeItem("embed.session.apiKey")
    sessionStorage.removeItem("wsAutoConnect")
    sessionStorage.removeItem("wsEnabled_reconocimiento")
    sessionStorage.removeItem("wsEnabled_ingresos")
    localStorage.removeItem("wsEnabled_reconocimiento")
    localStorage.removeItem("wsEnabled_ingresos")
    window.location.href = withBasename("/authentication/sign-in")
  };
  const ChagePass = () => {
    navigate("/cambiar-mi-contrasena")
  }

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
      PaperProps={{ sx: { maxWidth: 360, maxHeight: 480 } }}
    >
      <MDBox px={2} py={1} display="flex" justifyContent="space-between" alignItems="center">
        <MDTypography variant="button" fontWeight="bold" color={darkMode ? "white" : "dark"}>
          Notificaciones
        </MDTypography>
        {notifications.length > 0 && (
          <MDTypography
            variant="caption"
            color="info"
            sx={{ cursor: "pointer" }}
            onClick={markAllRead}
          >
            Marcar todas como leídas
          </MDTypography>
        )}
      </MDBox>
      <Divider sx={{ my: 0 }} />
      {notifications.length === 0 ? (
        <NotificationItem icon={<Icon>notifications_none</Icon>} title="Sin notificaciones" />
      ) : (
        notifications.map((n) => (
          <NotificationCard key={n.id} notification={n} onClick={markRead} />
        ))
      )}
    </Menu>
  )
  const renderMenuUser = () => (
    <Menu
      anchorEl={openMenuUser}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenuUser)}
      onClose={handleCloseMenuUser}
      sx={{ mt: 2 }}
    >
      <MDTypography
        variant="h6"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "top",
          textTransform: "uppercase",
        }}
      >
        {username}
      </MDTypography>
      <MDTypography
        variant="body2"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "top",
          fontVariant: "small-caps",
        }}
      >
        Acceso: {role}
      </MDTypography>
      <Divider />
      <NotificationItem
        onClick={ChagePass}
        icon={<Icon>password</Icon>}
        title="Cambiar mi contrase&ntilde;a"
      />
      <NotificationItem color="error" onClick={Logout} icon={<Icon>logout</Icon>} title="Cerrar sesi&oacute;n" />
    </Menu>
  )

  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main
      }

      return colorValue
    },
  })

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => ({
        ...navbar(theme, { transparentNavbar, absolute, light, darkMode }),
      })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? "white" : "inherit"}>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              <IconButton
                sx={navbarIconButton}
                size="small"
                onClick={handleOpenMenuUser}
                disableRipple
              >
                <Icon sx={iconsStyle}>account_circle</Icon>
              </IconButton>
              {renderMenuUser()}
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <MDBadge
                  badgeContent={unreadCount > 99 ? "+99" : unreadCount}
                  color="error"
                  size="xs"
                  circular
                  invisible={unreadCount === 0}
                >
                  <Icon sx={iconsStyle}>notifications</Icon>
                </MDBadge>
              </IconButton>
              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  )
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
}

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
}

export default DashboardNavbar
